import { Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowDown,
  lucideArrowUp,
  lucideBook,
  lucideDraftingCompass,
  lucideEllipsisVertical,
  lucideFileText,
  lucidePencilRuler,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';

import { CdkDrag, moveItemInArray } from '@angular/cdk/drag-drop';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { CardItemImports } from '../../../../shared/components/card-item';
import { ConfirmDialogComponent } from '../../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ExpansionPanelMenuAction } from '../../../../shared/components/expansion-panel/expansion-panel.component';
import { Production } from '../../../référentiel-métier-processus/processus-aptitude/models/production.model';
import { Ressource } from '../../models/ressource.model';
import { RéférentielMétierRessourcesProfessionnellesService } from '../../référentiel-métier-ressources-professionnelles.service';
import { ProductionStore } from '../../store/production.store';

@Component({
  selector: 'app-ressource-professionnelle-item',
  imports: [HlmIconImports, HlmButtonImports, HlmDropdownMenuImports, CardItemImports, CdkDrag],
  templateUrl: './ressource-professionnelle-item.component.html',
  providers: [
    provideIcons({
      lucideBook,
      lucideFileText,
      lucideDraftingCompass,
      lucidePencilRuler,
      lucideEllipsisVertical,
      lucideArrowUp,
      lucideArrowDown,
    }),
  ],
})
export class RessourceProfessionnelleItemComponent {
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #productionStore = inject(ProductionStore);
  readonly #ressourceProfessionnelleService = inject(RéférentielMétierRessourcesProfessionnellesService);

  readonly cartographie = input.required<Cartographie>();
  readonly production = input.required<Production>();
  readonly ressource = input.required<Ressource>();
  readonly peutModifier = input.required<boolean>();

  readonly ressourcesAction: ExpansionPanelMenuAction<Ressource>[] = [
    {
      icon: 'lucideTrash',
      label: 'Supprimer la ressource professionnelle',
      action: () => this.#supprimerRessource(),
    },
    {
      icon: 'lucideArrowUp',
      label: 'Faire monter',
      action: (ressource: Ressource | undefined) => this.#déplacerRessource(ressource, true),
    },
    {
      icon: 'lucideArrowDown',
      label: 'Faire descendre',
      action: (ressource: Ressource | undefined) => this.#déplacerRessource(ressource, false),
    },
  ];

  #supprimerRessource() {
    const ressource = this.ressource();

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer la ressource professionnelle`,
        contenu: `Êtes-vous sûr de vouloir supprimer la ressource professionnelle de type <span class="font-semibold">${ressource.type}</span> du nom de <span class="font-semibold">${ressource.nom}</span> ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#productionStore.modifier({
          idProduction: this.production().id,
          idCartographie: this.cartographie().id,
          data: {
            nom: this.production().nom,
            idAptitude: this.production().idAptitude,
            ressources: this.production().ressources.filter((x) => x.id !== ressource.id),
          },
          next: () => this.#ressourceProfessionnelleService.refreshData.next(true),
        });
      }
    });
  }

  #déplacerRessource(ressource: Ressource | undefined, isUp: boolean): void {
    if (!ressource) return;

    var indice = 0;
    if ((!isUp && ressource.indice === this.production().ressources.length - 1) || (isUp && ressource.indice === 0)) {
      indice = ressource.indice;
    } else if (!isUp) {
      indice = ressource.indice + 1;
    } else if (isUp) {
      indice = ressource.indice - 1;
    }

    const ordonnées = [...this.production().ressources.filter((x) => x.type === ressource.type)];
    moveItemInArray(ordonnées, ressource.indice, indice);

    const idProduction = this.production().id;

    this.#productionStore.ordonnerRessources({
      idProduction,
      type: ordonnées[0].type,
      idsRessources: ordonnées.map((r) => r.id),
      error: () => this.#productionStore.selection(idProduction),
    });
  }
}
